import { useState, useEffect, useMemo, useRef } from "react";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Drawer,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";

import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { useSWRConfig } from "swr";
import * as Sentry from "@sentry/react";

import ScoutMark from "../atoms/icons/ScoutMark";

import { fetchCompanyDataFromName } from "../../helpers/utils/apiCalls";
import {
  db,
  useAccountData,
  useAuth,
  useCachedFirebaseCrud,
  uploadFileToStorage
} from "../../helpers/firebase";
import { getAccountId } from "../../helpers/accountHelpers";
import { generateTempId } from "../../helpers/otherHelpers";
import { analyticsTrack } from "../../helpers/analytics";
import useExplorerLayoutContext from "../../helpers/contexts/explorerLayout";
import {
  completeSelfServiceLinking,
  linkCustomerToRelationshipFromParams,
  linkUserToVendorFromParams
} from "../../helpers/components/explorerOnboarding";

import { markdownComponents } from "./scout/ScoutMessages";

const TYPEWRITER_SPEED_MS = 18;
const SCOUT_THINK_DELAY_MS = 700;

const WELCOME_INTRO =
  "**Welcome to Aclymate Explorer!** I'm Scout, your AI guide on your sustainability journey.\n\nAclymate Explorer is a free tool that gives you a directional estimate of your company's carbon footprint — the perfect first step toward measuring and reducing your impact. I'll walk you through it: a few quick questions about your company, your sustainability goals, and any documents you'd like to share. Then I'll take you to your dashboard.";

const SELF_SERVICE_DESCRIPTION_PROMPT = `${WELCOME_INTRO}\n\nTo get us started — **what does your company do?**`;

const SELF_SERVICE_NAME_PROMPT =
  "Got it. Now, what's your company's **full legal name**? Use the formal name (e.g. *Counter Culture Coffee*, not *Counter Culture*) so I can find you accurately.";

const SELF_SERVICE_GOALS_PROMPT_TEMPLATE = (companyName) =>
  `Great — found you, **${companyName}**! Now let's talk about where you want to take this.\n\nIn a sentence or two: **what sustainability goals matter most to your company this year?** (e.g. reduce Scope 3 emissions 20%, win RFPs that require ESG data, prepare for CSRD reporting, etc.)`;

const INVITED_WELCOME_GOALS_PROMPT_TEMPLATE = (buyerName) =>
  `**Welcome to Aclymate Explorer!** I'm Scout, your AI guide on your sustainability journey.\n\n${
    buyerName
      ? `**${buyerName}** invited you to share your emissions data through Aclymate Explorer`
      : "You've been invited to share your emissions data through Aclymate Explorer"
  } — a free tool that helps you estimate and report your company's carbon footprint. I'm here to make it easy.\n\nLet's start with **what sustainability goals matter most to your company this year?** (e.g. reduce Scope 3 emissions 20%, win RFPs that require ESG data, prepare for CSRD reporting, etc.)`;

const DOCS_PROMPT =
  "Almost there. Got any prior emissions reports, utility bills, or sustainability documents you'd like to share? They're **optional** — but they help me get smarter about your footprint over time.";

const COMPLETION_PROMPT =
  "Perfect — you're all set! I'll always be in the **panel on the right** of your dashboard whenever you want to chat about your footprint, suppliers, products, or anything sustainability-related. Ready?";

const ESCAPE_HATCH_PROMPT =
  "No problem — let's get you to your dashboard. I'll be in the side panel whenever you want to come back to this.";

const RESUME_WELCOME =
  "Welcome back! Let's finish setting up your sustainability profile.";

const RESUME_GOALS_PROMPT =
  "In a sentence or two: **what sustainability goals matter most to your company this year?**";

const RESUME_DOCS_PROMPT =
  "Got any prior emissions reports, utility bills, or sustainability documents to upload? They're optional, but they help me get smarter about your footprint.";

const RESUME_COMPLETION_PROMPT =
  "Got it — your profile is updated. I'll be right here in the panel whenever you need me.";

const COMPANY_LOOKUP_THINKING =
  "Let me look that up for you — give me a few seconds.";

const COMPANY_LOOKUP_FAILED =
  "Hmm, I couldn't find your company with that description. Want to try again with more detail, or use a slightly different company name?";

const buildResumeMissing = (companyData) => [
  ...(!companyData?.sustainabilityGoals ? ["goals"] : []),
  ...(!companyData?.historicalDocuments?.length ? ["docs"] : [])
];

const findFirstMissingResumeStep = (companyData) => {
  if (!companyData?.sustainabilityGoals) return "sustainability_goals";
  if (!companyData?.historicalDocuments?.length) return "historical_documents";
  return null;
};

const buildStepDefs = ({ mode, resumeMissing, buyerName, vendorParams }) => {
  if (mode === "self_service") {
    return [
      {
        id: "description",
        kind: "text",
        scoutMessage: SELF_SERVICE_DESCRIPTION_PROMPT,
        field: "description",
        placeholder: "We make…",
        helperText: "A short description (e.g. specialty coffee roaster)"
      },
      {
        id: "name",
        kind: "text-with-lookup",
        scoutMessage: SELF_SERVICE_NAME_PROMPT,
        field: "name",
        placeholder: "Acme Corp"
      },
      {
        id: "goals",
        kind: "text",
        scoutMessage: null,
        field: "sustainabilityGoals",
        placeholder: "We want to…",
        multiline: true,
        showsEscape: true
      },
      {
        id: "documents",
        kind: "files",
        scoutMessage: DOCS_PROMPT,
        showsEscape: true
      },
      {
        id: "completion",
        kind: "completion",
        scoutMessage: COMPLETION_PROMPT
      }
    ];
  }
  if (mode === "invited") {
    return [
      {
        id: "goals",
        kind: "text",
        scoutMessage: INVITED_WELCOME_GOALS_PROMPT_TEMPLATE(buyerName),
        field: "sustainabilityGoals",
        placeholder: "We want to…",
        multiline: true,
        showsEscape: true
      },
      {
        id: "documents",
        kind: "files",
        scoutMessage: DOCS_PROMPT,
        showsEscape: true
      },
      {
        id: "completion",
        kind: "completion",
        scoutMessage: COMPLETION_PROMPT
      }
    ];
  }
  const resumeSteps = [
    ...(resumeMissing.includes("goals")
      ? [
          {
            id: "goals",
            kind: "text",
            scoutMessage: RESUME_GOALS_PROMPT,
            field: "sustainabilityGoals",
            placeholder: "We want to…",
            multiline: true
          }
        ]
      : []),
    ...(resumeMissing.includes("docs")
      ? [
          {
            id: "documents",
            kind: "files",
            scoutMessage: RESUME_DOCS_PROMPT
          }
        ]
      : []),
    {
      id: "completion",
      kind: "completion",
      scoutMessage: RESUME_COMPLETION_PROMPT
    }
  ];
  return resumeSteps;
};

const useTypewriter = (text, enabled = true) => {
  const [displayed, setDisplayed] = useState(enabled ? "" : text || "");
  const [done, setDone] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setDisplayed(text || "");
      setDone(true);
      return undefined;
    }
    setDisplayed("");
    setDone(false);
    if (!text) {
      setDone(true);
      return undefined;
    }
    const timeouts = text.split("").map((_, i) =>
      setTimeout(
        () => {
          setDisplayed(text.slice(0, i + 1));
          if (i === text.length - 1) setDone(true);
        },
        (i + 1) * TYPEWRITER_SPEED_MS
      )
    );
    return () => timeouts.forEach(clearTimeout);
  }, [text, enabled]);

  const skip = () => {
    setDisplayed(text || "");
    setDone(true);
  };

  return { displayed, done, skip };
};

const ScoutMessageBubble = ({ content, animate, onComplete, palette }) => {
  const { displayed, done, skip } = useTypewriter(content, animate);

  useEffect(() => {
    if (done && onComplete) onComplete();
  }, [done, onComplete]);

  return (
    <Box display="flex" justifyContent="flex-start" mb={2}>
      <Box
        display="flex"
        alignItems="flex-start"
        style={{ gap: 12, maxWidth: "100%" }}
      >
        <Box flexShrink={0} mt={0.5}>
          <ScoutMark size={32} />
        </Box>
        <Box
          onClick={animate && !done ? skip : undefined}
          px={2.5}
          py={1.5}
          style={{
            backgroundColor: palette.backgroundGray.dark,
            color: palette.text.primary,
            borderRadius: 12,
            cursor: animate && !done ? "pointer" : "default"
          }}
        >
          <Markdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
            {displayed || "​"}
          </Markdown>
        </Box>
      </Box>
    </Box>
  );
};

const UserBubble = ({ content, palette }) => (
  <Box display="flex" justifyContent="flex-end" mb={2}>
    <Box
      px={2.5}
      py={1.5}
      style={{
        backgroundColor: palette.secondary.main,
        color: palette.common.white,
        borderRadius: 12,
        maxWidth: "80%"
      }}
    >
      <Typography variant="body2" color="inherit">
        {content}
      </Typography>
    </Box>
  </Box>
);

const ThinkingBubble = ({ palette }) => (
  <Box display="flex" justifyContent="flex-start" mb={2}>
    <Box display="flex" alignItems="center" style={{ gap: 12 }}>
      <ScoutMark size={32} />
      <Box
        px={2}
        py={1.5}
        style={{
          backgroundColor: palette.backgroundGray.dark,
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          gap: 8
        }}
      >
        <CircularProgress size={14} />
        <Typography variant="body2" color="textSecondary">
          Scout is thinking…
        </Typography>
      </Box>
    </Box>
  </Box>
);

const ExplorerScoutOnboarding = ({
  mode = "self_service",
  vendorParams = {},
  onCompanyLinked,
  onCompleted
}) => {
  const [user] = useAuth();
  const theme = useTheme();
  const { palette } = theme;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { activateSnackbar } = useExplorerLayoutContext();
  const { updateAccountData } = useCachedFirebaseCrud();
  const [accountData] = useAccountData();
  const { mutate } = useSWRConfig();

  const buyerName = vendorParams.buyerName || "";
  const stepDefs = useMemo(
    () =>
      buildStepDefs({
        mode,
        resumeMissing: mode === "resume" ? buildResumeMissing(accountData) : [],
        buyerName,
        vendorParams
      }),
    [mode, accountData, buyerName, vendorParams]
  );

  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = stepDefs[stepIndex];

  const [chatMessages, setChatMessages] = useState([]);
  const [scoutThinking, setScoutThinking] = useState(false);
  const [scoutDone, setScoutDone] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [companyId, setCompanyId] = useState(
    mode === "invited" ? vendorParams.vendorId || "" : ""
  );
  const [formData, setFormData] = useState({
    description: "",
    name: "",
    sustainabilityGoals: accountData?.sustainabilityGoals || "",
    files: []
  });
  const [inputValue, setInputValue] = useState("");
  const [isLookupRunning, setIsLookupRunning] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [isLinking, setIsLinking] = useState(false);
  const [conversationId] = useState(() => String(generateTempId()));
  const greetingFired = useRef(false);
  const chatBottomRef = useRef(null);
  const chatMessagesRef = useRef([]);

  useEffect(() => {
    chatMessagesRef.current = chatMessages;
  }, [chatMessages]);

  useEffect(() => {
    if (greetingFired.current) return;
    greetingFired.current = true;
    if (mode === "resume" && stepDefs.length === 1) {
      setChatMessages([
        { role: "assistant", content: RESUME_COMPLETION_PROMPT, animate: true }
      ]);
      return;
    }
    if (mode === "resume") {
      setChatMessages([
        { role: "assistant", content: RESUME_WELCOME, animate: true }
      ]);
    }
    const first = stepDefs[0];
    if (first?.scoutMessage) {
      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: first.scoutMessage, animate: true }
      ]);
    }
    analyticsTrack("onboarding_step_entered", {
      step: first?.id,
      mode
    });
  }, [mode, stepDefs]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, scoutThinking]);

  const appendScoutMessage = (content) =>
    setChatMessages((prev) => [
      ...prev,
      { role: "assistant", content, animate: true }
    ]);

  const appendUserMessage = (content) =>
    setChatMessages((prev) => [...prev, { role: "user", content }]);

  const advanceToStep = (nextIndex, options = {}) => {
    const nextDef = stepDefs[nextIndex];
    if (!nextDef) return;
    analyticsTrack("onboarding_step_completed", {
      step: currentStep?.id,
      mode,
      companyId: companyId || null
    });
    analyticsTrack("onboarding_step_entered", {
      step: nextDef.id,
      mode
    });
    setStepIndex(nextIndex);
    setInputValue("");
    setScoutDone(false);
    if (nextDef.scoutMessage || options.scoutMessage) {
      setScoutThinking(true);
      setTimeout(() => {
        setScoutThinking(false);
        appendScoutMessage(options.scoutMessage || nextDef.scoutMessage);
      }, SCOUT_THINK_DELAY_MS);
    }
  };

  const handleDescriptionSubmit = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    appendUserMessage(trimmed);
    setFormData((prev) => ({ ...prev, description: trimmed }));
    advanceToStep(stepIndex + 1);
  };

  const runCompanyLookup = async (companyName) => {
    setIsLookupRunning(true);
    setScoutThinking(true);
    const resolved = await fetchCompanyDataFromName(
      companyName,
      formData.description,
      null,
      user
    );
    setScoutThinking(false);
    setIsLookupRunning(false);
    if (!resolved) {
      appendScoutMessage(COMPANY_LOOKUP_FAILED);
      return;
    }
    if (resolved.stripeCustomerId) {
      window.location.href = "/";
      return;
    }
    setCompanyData(resolved);
    setCompanyId(resolved.id);
    advanceToStep(stepIndex + 1, {
      scoutMessage: SELF_SERVICE_GOALS_PROMPT_TEMPLATE(
        resolved.name || companyName
      )
    });
  };

  const handleNameSubmit = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    appendUserMessage(trimmed);
    setFormData((prev) => ({ ...prev, name: trimmed }));
    setInputValue("");
    appendScoutMessage(COMPANY_LOOKUP_THINKING);
    setTimeout(() => runCompanyLookup(trimmed), SCOUT_THINK_DELAY_MS);
  };

  const handleGoalsSubmit = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    appendUserMessage(trimmed);
    setFormData((prev) => ({ ...prev, sustainabilityGoals: trimmed }));
    advanceToStep(stepIndex + 1);
  };

  const handleFilesSelected = (event) => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, ...selected]
    }));
    appendUserMessage(
      `Uploaded ${selected.length} file${selected.length === 1 ? "" : "s"}: ${selected.map((f) => f.name).join(", ")}`
    );
  };

  const removeFile = (index) =>
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));

  const uploadHistoricalDocuments = async (accountId) => {
    if (!formData.files.length) return { uploaded: [], failedCount: 0 };
    const settled = await Promise.allSettled(
      formData.files.map(async (file) => {
        const storagePath = `${accountId}/onboarding/historical-documents/${file.name}`;
        const downloadUrl = await uploadFileToStorage({
          path: storagePath,
          fileObject: file,
          hasUserConfirmedUpload: true
        });
        return {
          name: file.name,
          storagePath,
          downloadUrl,
          uploadedAt: Date.now(),
          uploadedBy: user?.uid || ""
        };
      })
    );
    const uploaded = settled
      .filter((r) => r.status === "fulfilled")
      .map((r) => r.value);
    const failures = settled.filter((r) => r.status === "rejected");
    failures.map((f) =>
      Sentry.captureException(f.reason, {
        tags: { feature: "explorer-onboarding-upload" },
        extra: { accountId }
      })
    );
    return { uploaded, failedCount: failures.length };
  };

  const finalizeLinking = async () => {
    if (mode === "resume") {
      return { success: true, accountId: getAccountId() };
    }
    if (mode === "invited") {
      if (vendorParams.relId) {
        return await linkCustomerToRelationshipFromParams(user, {
          relId: vendorParams.relId
        });
      }
      return await linkUserToVendorFromParams(user, {
        vendorId: vendorParams.vendorId,
        buyerId: vendorParams.buyerId,
        role: vendorParams.role
      });
    }
    return await completeSelfServiceLinking({
      user,
      companyData,
      buyerId: vendorParams.buyerId
    });
  };

  const readExistingForMerge = async (accountId) => {
    if (accountData?.id === accountId) return accountData;
    if (!accountId) return {};
    try {
      const snapshot = await getDoc(doc(db, `v3-companies/${accountId}`));
      return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : {};
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "explorer-onboarding-read-existing" },
        extra: { accountId }
      });
      return {};
    }
  };

  const persistOnboardingConversation = async (resolvedAccountId) => {
    if (mode === "resume") return;
    const turns = chatMessagesRef.current.map((msg) => ({
      role: msg.role,
      content: msg.content,
      timestamp: Date.now(),
      ...(msg.role === "assistant" ? { sources: [] } : {})
    }));
    if (!turns.length) return;
    const now = Date.now();
    const conversationRef = doc(
      db,
      `v3-companies/${resolvedAccountId}/agent-conversations/${conversationId}`
    );
    try {
      await setDoc(conversationRef, {
        turns,
        createdAt: now,
        updatedAt: now
      });
      window.sessionStorage.setItem(
        "pendingScoutConversationId",
        conversationId
      );
      mutate("agent-conversations");
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "explorer-onboarding-persist-conversation" },
        extra: { resolvedAccountId, conversationId, turnCount: turns.length }
      });
    }
  };

  const finalizeAndPersist = async ({ usedEscapeHatch }) => {
    setIsLinking(true);
    setLinkError("");
    try {
      const linkResult = await finalizeLinking();
      if (linkResult?.error) {
        setLinkError(
          linkResult.message || "We couldn't finish setting up your account."
        );
        return false;
      }
      if (linkResult?.stripeRedirect) {
        window.location.href = "/";
        return false;
      }
      const resolvedAccountId = linkResult.accountId;
      const uploadResult = usedEscapeHatch
        ? { uploaded: [], failedCount: 0 }
        : await uploadHistoricalDocuments(resolvedAccountId);
      if (uploadResult.failedCount > 0) {
        activateSnackbar({
          severity: "warning",
          message: `Couldn't upload ${uploadResult.failedCount} of ${formData.files.length} file${formData.files.length === 1 ? "" : "s"} — we'll continue setting up your account.`
        });
      }
      const existing = await readExistingForMerge(resolvedAccountId);
      const previousComplete = existing.onboardingComplete || {};
      const previousDismissals = existing.onboardingDismissals || 0;
      const mergedDocs = [
        ...(existing.historicalDocuments || []),
        ...uploadResult.uploaded
      ];
      const mergedGoals =
        formData.sustainabilityGoals || existing.sustainabilityGoals || "";
      await updateAccountData({
        sustainabilityGoals: mergedGoals,
        historicalDocuments: mergedDocs,
        onboardingComplete: { ...previousComplete, explorer: true },
        onboardingDismissals: usedEscapeHatch
          ? previousDismissals + 1
          : previousDismissals
      });
      await persistOnboardingConversation(resolvedAccountId);
      analyticsTrack("onboarding_completed", {
        mode,
        companyId: resolvedAccountId,
        hasSustainabilityGoals: !!formData.sustainabilityGoals,
        historicalDocumentCount: mergedDocs.length,
        usedEscapeHatch
      });
      analyticsTrack("explorer_onboarding_completed", {
        sustainabilityGoals: formData.sustainabilityGoals || "",
        historicalDocumentsCount: mergedDocs.length,
        hadBuyerContext: mode === "invited",
        timestamp: Date.now()
      });
      return resolvedAccountId;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "explorer-onboarding-finalize", mode },
        extra: { usedEscapeHatch }
      });
      setLinkError(
        "We couldn't finish setting up your account — please try again."
      );
      return false;
    } finally {
      setIsLinking(false);
    }
  };

  const handleDocsContinue = async () => {
    const resolvedAccountId = await finalizeAndPersist({
      usedEscapeHatch: false
    });
    if (resolvedAccountId === false) return;
    advanceToStep(stepIndex + 1);
  };

  const handleEscapeHatch = async () => {
    analyticsTrack("onboarding_escape_hatch_used", {
      step: currentStep?.id,
      mode,
      companyId: companyId || null
    });
    activateSnackbar({
      severity: "info",
      message: "No problem — Scout will check in with you again later."
    });
    appendScoutMessage(ESCAPE_HATCH_PROMPT);
    const resolvedAccountId = await finalizeAndPersist({
      usedEscapeHatch: true
    });
    if (resolvedAccountId === false) return;
    const completionIndex = stepDefs.findIndex((s) => s.kind === "completion");
    if (completionIndex >= 0) setStepIndex(completionIndex);
  };

  const handleCompletionGo = () => {
    if (onCompanyLinked && companyId) onCompanyLinked(companyId);
    if (onCompanyLinked && mode === "self_service" && companyData?.id) {
      onCompanyLinked(companyData.id);
    }
    if (onCompanyLinked && mode === "invited" && vendorParams.vendorId) {
      onCompanyLinked(vendorParams.vendorId);
    }
    if (onCompleted) onCompleted();
  };

  const handleResumeFinish = () => {
    if (onCompleted) onCompleted();
  };

  const handleKeyDown = (event, submitFn) => {
    if (event.key === "Enter" && !event.shiftKey && !currentStep?.multiline) {
      event.preventDefault();
      submitFn();
    }
  };

  const renderTextInput = (submitFn, disabled) => (
    <Box display="flex" alignItems="flex-end" style={{ gap: 8 }}>
      <TextField
        autoFocus
        fullWidth
        variant="outlined"
        size="small"
        placeholder={currentStep?.placeholder || "Type your answer…"}
        helperText={currentStep?.helperText}
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        onKeyDown={(event) => handleKeyDown(event, submitFn)}
        multiline={currentStep?.multiline}
        minRows={currentStep?.multiline ? 2 : 1}
        maxRows={currentStep?.multiline ? 5 : 1}
        disabled={disabled}
      />
      <IconButton
        color="primary"
        onClick={submitFn}
        disabled={disabled || !inputValue.trim()}
      >
        <SendIcon />
      </IconButton>
    </Box>
  );

  const renderFilesInput = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      style={{ gap: 12 }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ gap: 8 }}
      >
        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          disabled={isLinking}
        >
          Choose files
          <input
            type="file"
            multiple
            hidden
            onChange={handleFilesSelected}
            accept=".pdf,.csv,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg"
          />
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleDocsContinue}
          disabled={isLinking}
        >
          {isLinking
            ? "Finishing up…"
            : formData.files.length
              ? "Upload and finish"
              : "Skip and finish"}
        </Button>
      </Box>
      {formData.files.length > 0 && (
        <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
          {formData.files.map((file, index) => (
            <Chip
              key={`${file.name}-${index}`}
              label={file.name}
              onDelete={() => removeFile(index)}
              size="small"
            />
          ))}
        </Box>
      )}
      {!!linkError && (
        <Typography variant="body2" color="error">
          {linkError}
        </Typography>
      )}
    </Box>
  );

  const renderCompletionInput = () => (
    <Box display="flex" justifyContent="center">
      <Button
        variant="contained"
        color="secondary"
        size="large"
        onClick={mode === "resume" ? handleResumeFinish : handleCompletionGo}
      >
        {mode === "resume" ? "Done" : "Take me to my dashboard"}
      </Button>
    </Box>
  );

  const renderEscapeLink = () =>
    currentStep?.showsEscape && !isLinking ? (
      <Box display="flex" justifyContent="center" mt={1}>
        <Button
          size="small"
          color="inherit"
          onClick={handleEscapeHatch}
          style={{ color: palette.text.secondary, textTransform: "none" }}
        >
          I'll explore on my own
        </Button>
      </Box>
    ) : null;

  const renderInput = () => {
    if (!scoutDone && currentStep?.kind !== "completion") return null;
    if (
      currentStep?.kind === "text" ||
      currentStep?.kind === "text-with-lookup"
    ) {
      const submitFn =
        currentStep.id === "description"
          ? handleDescriptionSubmit
          : currentStep.id === "name"
            ? handleNameSubmit
            : handleGoalsSubmit;
      return renderTextInput(submitFn, isLookupRunning);
    }
    if (currentStep?.kind === "files") return renderFilesInput();
    if (currentStep?.kind === "completion") return renderCompletionInput();
    return null;
  };

  const renderChat = () =>
    chatMessages.map((msg, index) => {
      const isLast = index === chatMessages.length - 1;
      if (msg.role === "user") {
        return (
          <UserBubble
            key={`msg-${index}`}
            content={msg.content}
            palette={palette}
          />
        );
      }
      return (
        <ScoutMessageBubble
          key={`msg-${index}`}
          content={msg.content}
          animate={msg.animate && isLast}
          palette={palette}
          onComplete={isLast ? () => setScoutDone(true) : undefined}
        />
      );
    });

  const innerLayout = (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Box
        style={{
          maxHeight: "60vh",
          overflowY: "auto",
          paddingRight: 4
        }}
      >
        {renderChat()}
        {scoutThinking && <ThinkingBubble palette={palette} />}
        <div ref={chatBottomRef} />
      </Box>
      <Box>{renderInput()}</Box>
      {renderEscapeLink()}
    </Box>
  );

  if (mode === "resume") {
    if (isMobile) {
      return (
        <Drawer
          anchor="bottom"
          open
          PaperProps={{ sx: { height: "100%", padding: 2 } }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Continue setup</Typography>
            <IconButton onClick={onCompleted} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box pt={2}>{innerLayout}</Box>
        </Drawer>
      );
    }
    return (
      <Dialog open fullWidth maxWidth="sm">
        <DialogContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pb={1}
          >
            <Typography variant="h6">Continue setup</Typography>
            <IconButton onClick={onCompleted} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {innerLayout}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Box
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: palette.backgroundGray.light,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        zIndex: 1300,
        overflowY: "auto"
      }}
    >
      <Box style={{ width: "100%", maxWidth: 680 }}>
        <Grid container direction="column" spacing={3}>
          <Grid item>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <ScoutMark size={40} />
              <Box>
                <Typography variant="h6" color="textPrimary">
                  Scout
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Your AI sustainability guide
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item>{innerLayout}</Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ExplorerScoutOnboarding;

export { findFirstMissingResumeStep };
